import { Button } from "@/components/ui/button";
import {
  Award, Heart, Users, Target, Brain, Leaf, Star, Shield,
  Zap, Apple, Clock, Globe, CheckCircle, Handshake, Sparkles, type LucideIcon,
} from "lucide-react";

const PRINCIPLE_ICON_MAP: Record<string, LucideIcon> = {
  Heart, Target, Users, Brain, Leaf, Star, Shield, Award, Zap,
  Apple, Clock, Globe, CheckCircle, Handshake, Sparkles,
};
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { AccessibilityButton } from "@/components/AccessibilityButton";
import { Timeline } from "@/components/Timeline";
import { TeamGallery } from "@/components/TeamGallery";
import { useNossaHistoria } from "@/hooks/useNossaHistoria";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageVisibility, PAGE_VISIBILITY_KEYS } from "@/hooks/usePageVisibility";
import { PageUnavailable } from "@/components/ui/PageUnavailable";

const PKEYS    = ['p1', 'p2', 'p3', 'p4', 'p5'];
const PLEGACY  = ['humanization', 'sustainability', 'evidence'];
const PICONS   = ['Heart', 'Target', 'Users', 'Brain', 'Leaf'];
const PTITLES  = ['Humanização', 'Sustentabilidade', 'Evidência Científica', '', ''];
const PDESCS   = [
  'Acreditamos que o cuidado nutricional deve ser humano, empático e individualizado.',
  'Desenvolvemos estratégias nutricionais sustentáveis e adaptadas à sua rotina.',
  'Baseamos nossa prática em evidências científicas atualizadas.',
  '', '',
];

export default function NossaHistoria() {
  const { isVisible } = usePageVisibility();
  const { content, contentLoading } = useNossaHistoria();
  const { getSetting } = useSiteSettings();

  if (!isVisible(PAGE_VISIBILITY_KEYS.nossaHistoria)) {
    return <PageUnavailable />;
  }

  const getContent = (section: string, key: string, defaultValue: string = '') => {
    const item = content?.find(c => c.section === section && c.content_key === key);
    return item?.content_value?.text || defaultValue;
  };

  const heroTitle = getContent('hero', 'title', 'Quem Somos');
  const heroSubtitle = getContent('hero', 'subtitle', 'Conheça a jornada da Dra Brenda Ramos e sua missão de transformar vidas através da nutrição');
  const heroImageUrl = content?.find(c => c.section === 'hero' && c.content_key === 'image')?.content_value?.url as string | undefined;
  const storyTitle = getContent('story', 'title', 'Uma Jornada de Dedicação e Propósito');
  const storyP1    = getContent('story', 'paragraph1');
  const storyP2    = getContent('story', 'paragraph2');
  const crn        = getContent('story', 'crn', '3/63492');
  const crnLabel   = getContent('story', 'crn_label', 'Nutricionista registrada no Conselho Regional de Nutricionistas');

  const highlightedKey   = getSetting('principle_highlighted_key', 'p1');
  const disabledRaw      = getSetting('disabled_principle_keys', '[]');
  const disabledPrinciples: string[] = (() => { try { return JSON.parse(disabledRaw); } catch { return []; } })();

  const ctaRaw = getSetting('nossa_historia_cta', '');
  const cta = (() => {
    try { return ctaRaw ? JSON.parse(ctaRaw) : {}; } catch { return {}; }
  })();
  const ctaTitle      = cta.title      || 'Pronto para Iniciar sua Jornada?';
  const ctaDesc       = cta.description || 'Agende uma consulta e descubra como podemos transformar sua relação com a alimentação';
  const ctaBtnText    = cta.buttonText  || 'Falar com a Doutora';
  const ctaBtnUrl     = cta.buttonUrl   || 'https://wa.me/5511998874081';

  const principles = PKEYS.map((key, i) => {
    if (disabledPrinciples.includes(key)) return null;
    const item =
      content?.find(c => c.section === 'principle' && c.content_key === key) ??
      (i < PLEGACY.length ? content?.find(c => c.section === 'principle' && c.content_key === PLEGACY[i]) : undefined);
    if (!item && i >= 3) return null;
    return {
      key,
      title:       (item?.content_value?.title       as string) || PTITLES[i] || '',
      description: (item?.content_value?.description as string) || PDESCS[i]  || '',
      icon:        (item?.content_value?.icon        as string) || PICONS[i]  || 'Heart',
      highlighted: key === highlightedKey || (!highlightedKey && i === 0),
    };
  }).filter(Boolean) as { key: string; title: string; description: string; icon: string; highlighted: boolean }[];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="pt-24 bg-brand-primary py-14 sm:py-20">
        <div className="container mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 text-white/90 text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wide uppercase">
            <Award className="w-3.5 h-3.5" />
            Nutricionista CRN 3/63492
          </div>
          {contentLoading ? (
            <>
              <div className="h-12 w-64 bg-white/15 rounded-xl mx-auto mb-4 animate-pulse" />
              <div className="h-6 w-96 bg-white/10 rounded-lg mx-auto" />
            </>
          ) : (
            <>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-primary mb-4 leading-tight">
                {heroTitle}
              </h1>
              <p className="text-white/75 text-base sm:text-lg max-w-2xl mx-auto font-primary">
                {heroSubtitle}
              </p>
            </>
          )}
        </div>
      </section>

      {/* Story Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            {contentLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-10 w-3/4" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : (
              <div>
                <h2 className="text-3xl font-bold text-brand-primary mb-6">
                  {storyTitle}
                </h2>
                {storyP1 && (
                  <p className="text-lg font-text text-gray-600 mb-6 leading-relaxed">
                    {storyP1}
                  </p>
                )}
                {storyP2 && (
                  <p className="text-lg font-text text-gray-600 mb-6 leading-relaxed">
                    {storyP2}
                  </p>
                )}
                <div className="bg-brand-primary/5 border border-brand-primary/10 p-6 rounded-2xl">
                  <div className="flex items-center mb-2">
                    <div className="w-10 h-10 bg-brand-primary/10 rounded-xl flex items-center justify-center mr-3">
                      <Award className="w-5 h-5 text-brand-primary" />
                    </div>
                    <span className="font-bold text-brand-primary">CRN: {crn}</span>
                  </div>
                  <p className="text-brand-primary/70 text-sm font-text">
                    {crnLabel}
                  </p>
                </div>
              </div>
            )}
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-brand-primary/5 rounded-3xl" />
                <div className="relative w-72 h-72 bg-brand-primary rounded-3xl overflow-hidden flex items-center justify-center shadow-xl shadow-brand-primary/20">
                  {heroImageUrl ? (
                    <img src={heroImageUrl} alt="Nossa história" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[7rem] leading-none select-none">⚜️</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Princípios */}
          {contentLoading ? (
            <div className="flex flex-col lg:flex-row gap-0 mb-16">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="flex-1 h-56" />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl overflow-hidden shadow-lg flex flex-col lg:flex-row mb-16">
              {principles.map((p, i) => {
                const Icon = PRINCIPLE_ICON_MAP[p.icon] || Heart;
                const isFirst = i === 0;
                const isLast  = i === principles.length - 1;
                return p.highlighted ? (
                  <div key={p.key} className="flex-1 bg-brand-primary p-8 flex flex-col gap-4">
                    <div className="w-11 h-11 bg-white/15 rounded-xl flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-white font-primary mb-1.5">{p.title}</h3>
                      <p className="text-white/70 text-sm leading-relaxed">{p.description}</p>
                    </div>
                  </div>
                ) : (
                  <div
                    key={p.key}
                    className={`flex-1 bg-white p-8 flex flex-col gap-4 ${
                      !isFirst && !isLast ? 'border-y lg:border-y-0 lg:border-x border-gray-100' :
                      !isFirst ? 'border-t lg:border-t-0 lg:border-l border-gray-100' :
                      'border-b lg:border-b-0 lg:border-r border-gray-100'
                    }`}
                  >
                    <div className="w-11 h-11 bg-brand-primary/8 rounded-xl flex items-center justify-center">
                      <Icon className="w-5 h-5 text-brand-primary" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-800 font-primary mb-1.5">{p.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{p.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Timeline Section */}
          <div className="mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-primary mb-8 text-center">
              Nossa Trajetória
            </h2>
            <Timeline />
          </div>

          {/* Team Section */}
          <div className="mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-primary mb-4 text-center">
              Nossa Equipe
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 text-center mb-8 max-w-3xl mx-auto">
              Conheça os profissionais dedicados que tornam possível nosso trabalho de transformação através da nutrição
            </p>
            <TeamGallery />
          </div>

          {/* CTA Section */}
          <div className="text-center bg-brand-primary rounded-3xl py-12 px-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                {ctaTitle}
              </h2>
              <p className="text-white/80 mb-8 max-w-xl mx-auto">
                {ctaDesc}
              </p>
              <Button
                size="lg"
                className="bg-white text-brand-primary hover:bg-white/90 font-accent font-bold shadow-xl"
                onClick={() => window.open(ctaBtnUrl, '_blank')}
              >
                {ctaBtnText}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
      <AccessibilityButton />
    </div>
  );
}
